import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

const SocialContext = createContext(null);

export const SocialProvider = ({ children }) => {
  const { user } = useAuth();
  const [sessionToken, setSessionToken] = useState(null);
  const [connected, setConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(0);
  const [onlineIds, setOnlineIds] = useState([]);
  const [worldMessages, setWorldMessages] = useState([]);
  const [dmThreads, setDmThreads] = useState({}); // friendId -> [msg]
  const [unreadWorld, setUnreadWorld] = useState(0);
  const [unreadDm, setUnreadDm] = useState({}); // friendId -> count
  const [profileUserId, setProfileUserId] = useState(null); // profile viewer modal

  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const activeThreadRef = useRef(null); // 'world' | friendId | null

  useEffect(() => {
    const stored = window.sessionStorage.getItem("checkpoint_session_token");
    setSessionToken(user?.session_token || stored || null);
  }, [user]);

  const setActiveThread = useCallback((key) => {
    activeThreadRef.current = key;
    if (key === "world") setUnreadWorld(0);
    else if (key) setUnreadDm((u) => ({ ...u, [key]: 0 }));
  }, []);

  const openProfile = useCallback((id) => setProfileUserId(id || null), []);
  const closeProfile = useCallback(() => setProfileUserId(null), []);

  const connect = useCallback(() => {
    if (!sessionToken || !user) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;
    const ws = new WebSocket(`${WS_URL}/api/ws/social?token=${encodeURIComponent(sessionToken)}`);
    wsRef.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "presence_snapshot") {
          setOnlineCount(msg.online_count || 0);
          setOnlineIds(msg.online_ids || []);
        } else if (msg.type === "presence") {
          setOnlineCount(msg.online_count || 0);
          setOnlineIds((cur) => {
            const s = new Set(cur);
            if (msg.online) s.add(msg.user_id); else s.delete(msg.user_id);
            return Array.from(s);
          });
        } else if (msg.type === "world_chat") {
          setWorldMessages((cur) => [...cur.slice(-99), msg]);
          if (activeThreadRef.current !== "world" && msg.user_id !== user.user_id) setUnreadWorld((n) => n + 1);
        } else if (msg.type === "dm") {
          const other = msg.from === user.user_id ? msg.to : msg.from;
          setDmThreads((cur) => ({ ...cur, [other]: [...(cur[other] || []).slice(-99), msg] }));
          if (msg.from !== user.user_id && activeThreadRef.current !== other) {
            setUnreadDm((u) => ({ ...u, [other]: (u[other] || 0) + 1 }));
          }
        }
      } catch (e) { /* ignore */ }
    };
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      if (user && sessionToken && !reconnectRef.current) {
        reconnectRef.current = setTimeout(() => { reconnectRef.current = null; connect(); }, 2500);
      }
    };
    ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
  }, [sessionToken, user]);

  useEffect(() => {
    if (user && sessionToken) connect();
  }, [user, sessionToken, connect]);

  useEffect(() => () => {
    if (wsRef.current) { try { wsRef.current.close(); } catch (e) { /* ignore */ } }
    if (reconnectRef.current) clearTimeout(reconnectRef.current);
  }, []);

  // Tear down on logout
  useEffect(() => {
    if (!user && wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* ignore */ }
      wsRef.current = null;
      setConnected(false); setOnlineCount(0); setOnlineIds([]);
      setWorldMessages([]); setDmThreads({}); setUnreadWorld(0); setUnreadDm({});
    }
  }, [user]);

  const wsSend = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try { ws.send(JSON.stringify(payload)); return true; } catch (e) { return false; }
  }, []);

  const sendWorld = useCallback((text) => wsSend({ type: "world_chat", text }), [wsSend]);
  const sendDm = useCallback((to, text) => wsSend({ type: "dm", to, text }), [wsSend]);

  const loadWorldHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/world?limit=50`, { withCredentials: true });
      setWorldMessages(res.data.messages || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadDmHistory = useCallback(async (friendId) => {
    try {
      const res = await axios.get(`${API}/chat/dm/${friendId}?limit=50`, { withCredentials: true });
      setDmThreads((cur) => ({ ...cur, [friendId]: res.data.messages || [] }));
    } catch (e) { /* ignore */ }
  }, []);

  const totalUnread = unreadWorld + Object.values(unreadDm).reduce((a, b) => a + b, 0);
  const isOnline = useCallback((id) => onlineIds.includes(id), [onlineIds]);

  const value = useMemo(() => ({
    connected, onlineCount, onlineIds, isOnline,
    worldMessages, dmThreads, unreadWorld, unreadDm, totalUnread,
    sendWorld, sendDm, loadWorldHistory, loadDmHistory, setActiveThread,
    profileUserId, openProfile, closeProfile,
  }), [connected, onlineCount, onlineIds, isOnline, worldMessages, dmThreads, unreadWorld, unreadDm, totalUnread, sendWorld, sendDm, loadWorldHistory, loadDmHistory, setActiveThread, profileUserId, openProfile, closeProfile]);

  return <SocialContext.Provider value={value}>{children}</SocialContext.Provider>;
};

export const useSocial = () => {
  const ctx = useContext(SocialContext);
  if (!ctx) throw new Error("useSocial must be used within SocialProvider");
  return ctx;
};
